import {
    getAsRepairFieldDisplayValue,
    getAsRepairInboundBreakdownFields,
    getAsRepairReturnTotalFields,
    normalizeAsRepairFormConfig,
    parseQuantityValue,
} from './asRepairFormConfig';
import {
    AS_REPAIR_QTY_DETAIL_CATEGORIES,
    normalizeAsRepairQtyChartConfig,
} from './asRepairQtyChartConfig';

export { AS_REPAIR_QTY_DETAIL_CATEGORIES };

const normalizeCategoryToken = (text) => (
    String(text || '')
        .replace(/\s+/g, '')
        .replace(/[·・]/g, '')
        .toLowerCase()
);

const PAID_NORMAL_CATEGORY = '정상수량';
const FREE_NORMAL_OPERATION_CATEGORY = '작동정상';
const FREE_NORMAL_OPERATION_FIELD_TOKENS = new Set([
    '작동정상',
    '정상작동',
    '정상동작',
]);

export const matchAsRepairCategoryToFieldLabel = (fieldLabel, categoryTitle) => {
    const fl = normalizeCategoryToken(fieldLabel);
    const ct = normalizeCategoryToken(categoryTitle);
    if (!fl || !ct) {
        return false;
    }
    if (fl === ct) {
        return true;
    }
    // 유상「정상수량」: 라벨이 정확히 정상류인 필드만 (무상 작동정상과 분리)
    if (ct === normalizeCategoryToken(PAID_NORMAL_CATEGORY)) {
        return fl === normalizeCategoryToken(PAID_NORMAL_CATEGORY)
            || fl === normalizeCategoryToken('정상');
    }
    // 무상「작동정상」: 동작·작동 표기 모두 허용, 유상 정상류와 분리
    if (ct === normalizeCategoryToken(FREE_NORMAL_OPERATION_CATEGORY)) {
        return FREE_NORMAL_OPERATION_FIELD_TOKENS.has(fl);
    }
    // 무상「COIL」: COIL간섭·COIL 간섭 등 COIL 계열 필드 매칭
    if (ct === normalizeCategoryToken('COIL')) {
        return fl.includes('coil');
    }
    // 유상「LCD파손」: 파손만 매칭 (무상 LCD고장·LCD냉땜 제외)
    if (ct === normalizeCategoryToken('LCD파손') || ct === normalizeCategoryToken('LCD')) {
        return fl.includes('lcd') && fl.includes('파손') && !fl.includes('고장');
    }
    // 무상「LCD고장」: 고장만 매칭 (유상 LCD파손·LCD냉땜 제외)
    if (ct === normalizeCategoryToken('LCD고장')) {
        return fl.includes('lcd') && fl.includes('고장') && !fl.includes('파손');
    }
    // 유상「커넥터파손」: 파손 항목만 매칭 (무상 커넥터교체 제외)
    if (ct === normalizeCategoryToken('커넥터파손')) {
        return fl.includes('커넥터') && fl.includes('파손') && !fl.includes('교체');
    }
    // 무상「커넥터교체」: 교체 항목만 매칭 (유상 커넥터파손 제외)
    if (ct === normalizeCategoryToken('커넥터교체')) {
        return fl.includes('커넥터') && fl.includes('교체') && !fl.includes('파손');
    }
    // 유상「Micom파손」: 파손만 매칭 (무상 Micom고장과 분리)
    if (ct === normalizeCategoryToken('Micom파손')) {
        const hasMicom = fl.includes('micom') || fl.includes('마이컴') || fl.includes('마이콤');
        return hasMicom && fl.includes('파손') && !fl.includes('고장');
    }
    // 무상「Micom고장」: 고장만 매칭 (유상 Micom파손과 분리)
    if (ct === normalizeCategoryToken('Micom고장')) {
        const hasMicom = fl.includes('micom') || fl.includes('마이컴') || fl.includes('마이콤');
        return hasMicom && fl.includes('고장') && !fl.includes('파손');
    }
    return fl.includes(ct) || ct.includes(fl);
};

const fieldMatchesCategory = (field, categoryTitle) => (
    matchAsRepairCategoryToFieldLabel(field?.label, categoryTitle)
    || matchAsRepairCategoryToFieldLabel(field?.key, categoryTitle)
    || matchAsRepairCategoryToFieldLabel(
        String(field?.key || '').replace(/^custom_/i, ''),
        categoryTitle,
    )
);

const sumQtyAcrossRows = (rows, field, config) => {
    if (!field) {
        return 0;
    }
    const list = Array.isArray(rows) ? rows : [];
    return list.reduce(
        (sum, row) => sum + parseQuantityValue(
            getAsRepairFieldDisplayValue(field, row, config) ?? 0,
        ),
        0,
    );
};

const sumQtyAcrossFields = (rows, fields, config) => (
    (Array.isArray(fields) ? fields : []).reduce(
        (sum, field) => sum + sumQtyAcrossRows(rows, field, config),
        0,
    )
);

const getAllCategoryTitles = (chartConfig) => {
    const titles = new Set(Object.values(AS_REPAIR_QTY_DETAIL_CATEGORIES).flat());
    normalizeAsRepairQtyChartConfig(chartConfig).groups.forEach((g) => {
        g.categories.forEach((c) => titles.add(c.title));
    });
    return [...titles];
};

const isDetailCategoryField = (field, categoryTitles) => (
    categoryTitles.some((title) => fieldMatchesCategory(field, title))
);

const collectExcludeKeysForDetailMatch = (config, listSummary, categoryTitles) => {
    const fields = normalizeAsRepairFormConfig(config).fields;
    const {
        receivedField,
        shortageField,
        returnField,
        paidField,
        freeField,
    } = getAsRepairInboundBreakdownFields(
        config,
        listSummary,
    );
    const exclude = new Set();
    [receivedField, shortageField, returnField, paidField, freeField].forEach((f) => {
        if (f?.key) {
            exclude.add(f.key);
        }
    });
    (listSummary?.columns || []).forEach((col) => {
        if (col.id === 'inProgressQty' || col.id === 'doneQty') {
            if (col.field?.key) {
                exclude.add(col.field.key);
            }
        }
    });
    fields.forEach((f) => {
        if (f.inputType !== 'quantity' && f.inputType !== 'auto') {
            return;
        }
        if (exclude.has(f.key) || isDetailCategoryField(f, categoryTitles)) {
            return;
        }
        const nl = normalizeCategoryToken(f.label);
        if (
            nl.includes('전체')
            || nl.includes('실입고')
            || nl.includes('진행중')
            || nl.includes('수리완료')
            || nl.includes('완료수량')
            || nl.includes('부족')
            || (nl.includes('반품') && !nl.includes('폐기'))
            || nl.includes('유상수리')
            || nl.includes('무상수리')
            || (nl.includes('입고') && !nl.includes('폐기'))
        ) {
            exclude.add(f.key);
        }
    });
    return exclude;
};

const assignFieldsToCategories = (fields, categories, excludeKeys) => {
    const pool = fields.filter(
        (f) => (f.inputType === 'quantity' || f.inputType === 'auto') && !excludeKeys.has(f.key),
    );
    const sortedCategories = [...categories].sort(
        (a, b) => normalizeCategoryToken(b).length - normalizeCategoryToken(a).length,
    );
    const assignedFieldKeys = new Set();
    const map = {};
    sortedCategories.forEach((cat) => {
        map[cat] = [];
    });

    pool.forEach((field) => {
        if (assignedFieldKeys.has(field.key)) {
            return;
        }
        const cat = sortedCategories.find((title) => fieldMatchesCategory(field, title));
        if (cat) {
            map[cat].push(field);
            assignedFieldKeys.add(field.key);
        }
    });

    // 상위 집계 필드로 제외된 경우에도 세부 카테고리(Micom고장 등)는 다시 연결
    const excludedPool = fields.filter(
        (f) => (f.inputType === 'quantity' || f.inputType === 'auto')
            && excludeKeys.has(f.key)
            && !assignedFieldKeys.has(f.key),
    );
    excludedPool.forEach((field) => {
        if (assignedFieldKeys.has(field.key)) {
            return;
        }
        const cat = sortedCategories.find((title) => fieldMatchesCategory(field, title));
        if (cat) {
            map[cat].push(field);
            assignedFieldKeys.add(field.key);
        }
    });

    return map;
};

/** 수량 필드 값이 0보다 큰 행 ID 목록 */
export const collectRowIdsForQtyField = (rows, config, field) => {
    if (!field) {
        return [];
    }
    const list = Array.isArray(rows) ? rows : [];
    const ids = [];
    list.forEach((row) => {
        const qty = parseQuantityValue(
            getAsRepairFieldDisplayValue(field, row, config) ?? 0,
        );
        if (qty > 0 && row?.id != null) {
            ids.push(row.id);
        }
    });
    return [...new Set(ids)];
};

/** 여러 수량 필드 중 하나라도 0보다 크면 포함 */
export const collectRowIdsForQtyFields = (rows, config, fields) => {
    const idSet = new Set();
    (Array.isArray(fields) ? fields : []).forEach((field) => {
        collectRowIdsForQtyField(rows, config, field).forEach((id) => idSet.add(id));
    });
    return [...idSet];
};

const LEGACY_GROUP_LABELS = {
    totalQty: '전체수량',
    shortage: '수량부족',
    return: '반품수량',
    paid: '유상수리수량',
    free: '무상수리수량',
    done: '수리완료수량',
};

const resolveChartGroup = (chartConfig, groupId) => {
    const group = normalizeAsRepairQtyChartConfig(chartConfig).groups.find((g) => g.id === groupId);
    if (group) {
        return group;
    }
    const titles = AS_REPAIR_QTY_DETAIL_CATEGORIES[groupId];
    if (!titles) {
        return null;
    }
    return {
        id: groupId,
        title: LEGACY_GROUP_LABELS[groupId] || groupId,
        color: '#64748b',
        visible: true,
        fieldKeys: [],
        categories: titles.map((title) => ({ title, fieldKeys: [] })),
    };
};

/** 그룹 합계 필드 — 그래프 관리에서 지정한 필드 우선, 없으면 기본 그룹은 라벨로 자동 탐색 */
const resolveGroupParentFields = (config, listSummary, group) => {
    const fields = normalizeAsRepairFormConfig(config).fields;
    const explicit = (group?.fieldKeys || [])
        .map((key) => fields.find((f) => f.key === key))
        .filter(Boolean);
    if (explicit.length > 0) {
        return explicit;
    }
    if (group?.id === 'return') {
        return getAsRepairReturnTotalFields(config, listSummary);
    }
    const breakdown = getAsRepairInboundBreakdownFields(config, listSummary);
    const auto = {
        totalQty: breakdown.receivedField,
        shortage: breakdown.shortageField,
        done: breakdown.doneField,
        paid: breakdown.paidField,
        free: breakdown.freeField,
    }[group?.id];
    return auto ? [auto] : [];
};

/** 하위 항목별 연결 필드 — 지정 필드 우선, 없으면 라벨 자동 매칭 */
const resolveGroupCategoryFields = (config, listSummary, group, chartConfig, parentFields) => {
    const fields = normalizeAsRepairFormConfig(config).fields;
    const byKey = new Map(fields.map((f) => [f.key, f]));
    const categories = group?.categories || [];
    const explicitKeys = new Set();
    categories.forEach((c) => c.fieldKeys.forEach((k) => explicitKeys.add(k)));
    const autoTitles = categories.filter((c) => c.fieldKeys.length === 0).map((c) => c.title);
    const assignment = autoTitles.length > 0
        ? assignFieldsToCategories(
            fields.filter((f) => !explicitKeys.has(f.key)),
            autoTitles,
            collectExcludeKeysForDetailMatch(config, listSummary, getAllCategoryTitles(chartConfig)),
        )
        : {};
    return categories.map((c) => {
        const explicit = c.fieldKeys.length > 0;
        let matchedFields = explicit
            ? c.fieldKeys.map((k) => byKey.get(k)).filter(Boolean)
            : (assignment[c.title] || []);
        if (!explicit && matchedFields.length === 0 && categories.length === 1 && parentFields.length > 0) {
            matchedFields = parentFields;
        }
        return { title: c.title, explicit, matchedFields };
    });
};

/** 그래프 그룹에 해당하는 행 ID */
export const collectRowIdsForQtyGroup = (rows, config, listSummary, groupId, chartConfig = null) => {
    const group = resolveChartGroup(chartConfig, groupId);
    if (!group) {
        return [];
    }
    const parentFields = resolveGroupParentFields(config, listSummary, group);
    if (parentFields.length > 0) {
        return collectRowIdsForQtyFields(rows, config, parentFields);
    }
    const categoryFields = resolveGroupCategoryFields(config, listSummary, group, chartConfig, parentFields);
    return collectRowIdsForQtyFields(rows, config, categoryFields.flatMap((c) => c.matchedFields));
};

/** 그래프 세부사항 상위 그룹(수량부족·반품·유상·무상 등) 합계 */
export const aggregateAsRepairQtyChartGroups = (rows, config, listSummary, chartConfig, receivedTotal = 0) => (
    normalizeAsRepairQtyChartConfig(chartConfig).groups
        .filter((g) => g.visible)
        .map((group) => {
            const parentFields = resolveGroupParentFields(config, listSummary, group);
            const categoryFields = parentFields.length > 0
                ? null
                : resolveGroupCategoryFields(config, listSummary, group, chartConfig, parentFields);
            const totalFields = parentFields.length > 0
                ? parentFields
                : categoryFields.flatMap((c) => c.matchedFields);
            const value = parentFields.length > 0
                ? sumQtyAcrossFields(rows, parentFields, config)
                : categoryFields.reduce((sum, c) => sum + sumQtyAcrossFields(rows, c.matchedFields, config), 0);
            const percentOfReceived = receivedTotal > 0
                ? Math.round((value / receivedTotal) * 1000) / 10
                : 0;
            const rowIds = collectRowIdsForQtyFields(rows, config, totalFields);
            return {
                id: group.id,
                label: group.title,
                value,
                percentOfReceived,
                percentLabel: receivedTotal > 0 ? `실입고 대비 ${percentOfReceived}%` : '-',
                color: group.color,
                fieldFound: totalFields.length > 0,
                rowIds,
                rowCount: rowIds.length,
            };
        })
);

/** 그래프 관리 화면용 — 그룹·하위 항목이 실제 어떤 필드에 연결되는지 */
export const previewAsRepairQtyChartFieldMatches = (config, listSummary, chartConfig) => {
    const result = {};
    normalizeAsRepairQtyChartConfig(chartConfig).groups.forEach((group) => {
        const parentFields = resolveGroupParentFields(config, listSummary, group);
        result[group.id] = {
            parentFields,
            parentExplicit: group.fieldKeys.length > 0,
            categories: resolveGroupCategoryFields(config, listSummary, group, chartConfig, parentFields),
        };
    });
    return result;
};

/** 목록 행의 모델명 (리스트 요약 설정 기준) */
export const getAsRepairRowModelName = (row, listSummary, config) => {
    const modelCol = listSummary?.columns?.find((c) => c.id === 'modelName');
    if (modelCol?.field) {
        const v = getAsRepairFieldDisplayValue(modelCol.field, row, config);
        const text = v == null ? '' : String(v).trim();
        if (text) {
            return text;
        }
    }
    const fallback = row?.productName ?? row?.product_name ?? '';
    const text = String(fallback).trim();
    return text || '미입력';
};

/** 하위 항목(폐기·LCD 등)별 모델명 수량·건수 집계 */
export const aggregateCategoryQtyByModel = (rows, config, listSummary, matchedFields, categoryTotal) => {
    const list = Array.isArray(rows) ? rows : [];
    const fields = Array.isArray(matchedFields) ? matchedFields : [];
    if (fields.length === 0) {
        return { items: [], totalQuantity: 0 };
    }

    const byModel = new Map();
    list.forEach((row) => {
        const rowQty = fields.reduce(
            (sum, field) => sum + parseQuantityValue(
                getAsRepairFieldDisplayValue(field, row, config) ?? 0,
            ),
            0,
        );
        if (rowQty <= 0) {
            return;
        }
        const modelName = getAsRepairRowModelName(row, listSummary, config);
        const prev = byModel.get(modelName) || { quantity: 0, rowCount: 0, rowIds: [] };
        const rowId = row?.id;
        byModel.set(modelName, {
            quantity: prev.quantity + rowQty,
            rowCount: prev.rowCount + 1,
            rowIds: rowId != null ? [...prev.rowIds, rowId] : prev.rowIds,
        });
    });

    const totalQuantity = categoryTotal > 0
        ? categoryTotal
        : Array.from(byModel.values()).reduce((s, v) => s + v.quantity, 0);

    const items = Array.from(byModel.entries())
        .map(([modelName, stats]) => {
            const percentOfCategory = totalQuantity > 0
                ? Math.round((stats.quantity / totalQuantity) * 1000) / 10
                : 0;
            return {
                modelName,
                quantity: stats.quantity,
                rowCount: stats.rowCount,
                rowIds: stats.rowIds || [],
                percentOfCategory,
                percentLabel: totalQuantity > 0 ? `${percentOfCategory}%` : '-',
            };
        })
        .sort((a, b) => (
            b.quantity - a.quantity
            || b.rowCount - a.rowCount
            || a.modelName.localeCompare(b.modelName, 'ko')
        ));

    return { items, totalQuantity };
};

/** 그룹(전체/수량부족/반품/유상/무상/수리완료) 하위 항목 합계 및 상위 수량 대비 % */
export const aggregateAsRepairQtyGroupDetail = (rows, config, listSummary, groupId, options = {}) => {
    const { includeModelBreakdown = true, chartConfig = null } = options;
    const group = resolveChartGroup(chartConfig, groupId);
    if (!group) {
        return { parentLabel: '', parentTotal: 0, items: [] };
    }

    const parentFields = resolveGroupParentFields(config, listSummary, group);
    const categoryFields = resolveGroupCategoryFields(config, listSummary, group, chartConfig, parentFields);
    const parentTotal = parentFields.length > 0
        ? sumQtyAcrossFields(rows, parentFields, config)
        : categoryFields.reduce((sum, c) => sum + sumQtyAcrossFields(rows, c.matchedFields, config), 0);

    const items = categoryFields.map(({ title, matchedFields }) => {
        const value = sumQtyAcrossFields(rows, matchedFields, config);
        const percentOfParent = parentTotal > 0
            ? Math.round((value / parentTotal) * 1000) / 10
            : 0;
        const rowIds = collectRowIdsForQtyFields(rows, config, matchedFields);
        return {
            id: `${groupId}-${normalizeCategoryToken(title)}`,
            title,
            label: matchedFields.length === 1
                ? matchedFields[0].label
                : matchedFields.length > 1
                    ? `${title} (${matchedFields.length}항목)`
                    : title,
            value,
            rowIds,
            rowCount: rowIds.length,
            percentOfParent,
            percentLabel: parentTotal > 0 ? `${percentOfParent}%` : '-',
            fieldFound: matchedFields.length > 0,
            matchedFields,
            modelBreakdown: includeModelBreakdown
                ? aggregateCategoryQtyByModel(
                    rows,
                    config,
                    listSummary,
                    matchedFields,
                    value,
                )
                : { items: [], totalQuantity: value },
        };
    });

    return {
        parentLabel: group.title,
        parentTotal,
        items,
    };
};
